<script setup>
import { ref, watch } from 'vue';
import { payStackDeposit, verifyBeneficiary } from "@/services/api_services"; // ← assume verifyBeneficiary is imported here
import { useRoute } from "vue-router";

const amount = ref('');
const phoneNumber = ref('');
const name = ref("");
const beneficiaryPhoneNumber = ref('');
const contributeForSomeoneElse = ref(false);
const beneficiaryVerified = ref(false);
const beneficiaryName = ref(null);
const verifying = ref(false);
const verificationError = ref(null);

const loading = ref(false);
const error = ref(null);
const success = ref(null);

const route = useRoute();
const fundraiserId = route.params.fundraiserId;
const fundraiserName = route.params.fundraiserName;

// Reset verification when beneficiary phone changes
watch(beneficiaryPhoneNumber, () => {
  beneficiaryVerified.value = false;
  beneficiaryName.value = null;
  verificationError.value = null;
});

const verify = async () => {
  const phone = beneficiaryPhoneNumber.value.trim();
  if (!phone || phone.length < 9) {
    verificationError.value = 'Please enter a valid phone number (at least 9 digits)';
    return;
  }

  verifying.value = true;
  verificationError.value = null;
  beneficiaryName.value = null;
  beneficiaryVerified.value = false;

  try {
    const result = await verifyBeneficiary(phone);
    beneficiaryName.value = result.name || 'User found';
    beneficiaryVerified.value = true;
  } catch (err) {
    if (err.response?.status === 400 && err.response?.data?.message === 'The user does not exist') {
      verificationError.value = 'No user found with this phone number';
    } else {
      verificationError.value = err.response?.data?.message || 'Failed to verify beneficiary. Please try again.';
    }
    beneficiaryVerified.value = false;
  } finally {
    verifying.value = false;
  }
};

const handleContribution = async () => {
  error.value = null;
  success.value = null;

  if (!amount.value || !phoneNumber.value) {
    error.value = 'Amount and phone number are required.';
    return;
  }

  if (contributeForSomeoneElse.value) {
    if (!beneficiaryPhoneNumber.value.trim()) {
      error.value = 'Please enter beneficiary phone number.';
      return;
    }
    if (!beneficiaryVerified.value) {
      error.value = 'Please verify the beneficiary phone number first.';
      return;
    }
  }

  loading.value = true;

  try {
    const contributorName = name.value.trim() || "Anonymous";

    const response = await payStackDeposit(
        contributorName,
        amount.value,
        phoneNumber.value,
        fundraiserId,
        contributeForSomeoneElse.value ? beneficiaryPhoneNumber.value.trim() : null
    );

    if (response && response.result?.authorizationUrl) {
      const paymentUrl = response.result.authorizationUrl;
      console.log("Payment URL:", paymentUrl);
      window.open(paymentUrl, '_blank');
      success.value = 'Redirecting to payment page...';
    } else {
      error.value = 'Failed to initiate payment. Please try again.';
    }
  } catch (e) {
    console.error('Payment error:', e);
    error.value = e.response?.data?.message || 'Failed to process your contribution. Please try again.';
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="page-container">
    <div class="contribution-form-container">
      <h1 class="page-title">Paystack Contribution for {{ fundraiserName }}</h1>
      <p class="form-instructions">Fill out the form below to contribute via Paystack</p>

      <div class="form-group">
        <label for="name">Full Name (optional)</label>
        <input
            id="name"
            v-model="name"
            type="text"
            placeholder="Enter your full name or leave blank for Anonymous"
            class="form-input"
        />
      </div>

      <div class="form-group">
        <label for="amount">How much do you want to deposit?</label>
        <input
            id="amount"
            v-model="amount"
            type="number"
            placeholder="Enter amount (KES)"
            min="1"
            class="form-input"
        />
      </div>

      <div class="form-group">
        <label for="phone">Phone Number (used for payment)</label>
        <input
            id="phone"
            v-model="phoneNumber"
            type="tel"
            placeholder="e.g., 07XXXXXXXX"
            class="form-input"
        />
      </div>

      <div class="form-group checkbox-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="contributeForSomeoneElse" />
          Contribute for someone else
        </label>
      </div>

      <div v-if="contributeForSomeoneElse" class="form-group beneficiary-section">
        <label for="beneficiary">Beneficiary Phone Number</label>
        <div class="input-with-button">
          <input
              id="beneficiary"
              v-model="beneficiaryPhoneNumber"
              type="tel"
              placeholder="e.g., 07XXXXXXXX"
              class="form-input"
          />
          <button
              type="button"
              class="verify-button"
              :disabled="verifying || !beneficiaryPhoneNumber.trim() || beneficiaryPhoneNumber.length < 9"
              @click="verify"
          >
            {{ verifying ? 'Verifying...' : 'Verify' }}
          </button>
        </div>

        <div v-if="beneficiaryVerified" class="verification-success">
          <svg class="icon success-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L10 10.586 8.707 9.293z" clip-rule="evenodd" />
          </svg>
          Verified: {{ beneficiaryName }}
        </div>

        <div v-if="verificationError" class="verification-error">
          {{ verificationError }}
        </div>

      </div>

      <button
          class="submit-button"
          :disabled="loading"
          @click="handleContribution"
          :class="{ 'button-loading': loading }"
      >
        <span v-if="!loading">Contribute Now</span>
        <span v-else class="loading-text">Processing...</span>
      </button>

      <div v-if="error" class="message error-message">
        <svg class="icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ error }}
      </div>

      <div v-if="success" class="message success-message">
        <svg class="icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        {{ success }}
      </div>

      <div class="security-notice">
        <svg class="lock-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
        </svg>
        <span>Your information is secure and will not be shared</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(
      135deg,
      rgba(203, 217, 196, 1) 0%,
      rgba(249, 247, 194, 1) 50%,
      rgba(249, 213, 206, 1) 100%
  );
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.contribution-form-container {
  width: 100%;
  max-width: 480px;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 16px;
  box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.08),
      0 4px 12px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  text-align: center;
  color: #111827;
  line-height: 1.3;
}

.form-instructions {
  text-align: center;
  margin-bottom: 2rem;
  color: #6b7280;
  font-size: 0.95rem;
}

.form-group {
  margin-bottom: 1.75rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.95rem;
}

.form-input {
  width: 100%;
  padding: 0.95rem 1.35rem;  /* Increased padding for better comfort */
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background-color: #f9fafb;
  box-sizing: border-box; /* Ensures padding is included in width */
}

.form-input:focus {
  border-color: #10b981;
  outline: none;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  background-color: white;
}

.form-input::placeholder {
  color: #9ca3af;
}

.submit-button {
  width: 100%;
  padding: 1.1rem;  /* Slightly increased padding */
  margin-top: 0.5rem;
  background: linear-gradient(to right, #10b981, #059669);
  color: white;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(16, 185, 129, 0.2);
}

.submit-button:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}

.button-loading {
  color: transparent;
}

.loading-text {
  position: absolute;
  left: 0;
  right: 0;
  color: white;
}

.message {
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.error-message {
  background-color: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.success-message {
  background-color: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.icon {
  width: 1.25rem;
  height: 1.25rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.security-notice {
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  font-size: 0.85rem;
  color: #6b7280;
  padding: 0.75rem;
  background-color: #f8fafc;
  border-radius: 8px;
}

.lock-icon {
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
  color: #6b7280;
}

@media (max-width: 600px) {
  .page-container {
    padding: 1.25rem;
  }

  .contribution-form-container {
    padding: 1.75rem;
  }

  .page-title {
    font-size: 1.5rem;
  }
}
.checkbox-group {
  margin-bottom: 1.5rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  color: #374151;
  cursor: pointer;
}

.checkbox-label input {
  margin-right: 0.75rem;
  width: 1.1rem;
  height: 1.1rem;
}

.beneficiary-section {
  background: #f8fafc;
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.75rem;
  border: 1px solid #e5e7eb;
}

.input-with-button {
  display: flex;
  gap: 0.75rem;
}

.verify-button {
  padding: 0 1.25rem;
  background: #16a34a;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.verify-button:hover:not(:disabled) {
  background: #16a34a;
}

.verify-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.verification-success {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #ecfdf5;
  color: #065f46;
  border-radius: 8px;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.verification-error {
  margin-top: 0.75rem;
  color: #dc2626;
  font-size: 0.9rem;
}

.help-text {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #6b7280;
  font-style: italic;
}

.success-icon {
  width: 1.1rem;
  height: 1.1rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
}
</style>